import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Customer, Quote, QuoteItem, QuoteSection, DEFAULT_QUOTE_SECTIONS } from '../../types';
import {
  QUOTE_EXCLUDED_LABEL,
  calculateQuoteTotalsDetailed,
  computePosNumbers,
  fmtPercentDE,
  quoteTotalsMode,
  isQuoteItemActive,
  isQuoteItemExcluded,
  lineTotal,
  round2,
  intervalSummary,
  totalInspectionCount,
} from '../quoteLogic';
import { QuoteRichLine, effectiveQuoteRich, layoutQuoteRich } from '../quoteRichText';
import { supplementPdfReferenceLine } from '../quoteSupplementLogic';
import { COLORS, formatDate } from './pdfUtils';
import {
  A4_GEOMETRY,
  CompanyInfo,
  cityFromAddress,
  createLetterhead,
  drawFooters,
  drawPage1Header,
  fmtEur,
  fmtNum,
  longDate,
} from './letterhead';

// Briefkopf, Briefpapier, Fusszeile und die Zahlen-/Datumsformatierung liegen
// seit dem Rechnungsmodul in letterhead.ts — dieselbe Implementierung fuer
// Angebot und Rechnung, damit "gleiches Briefpapier" nicht von einer Kopie
// abhaengt. Re-Export, damit bestehende Importe unveraendert weiterlaufen.
export type { CompanyInfo };

// Platzhalter in der Pos-Spalte einer gestrichenen Zeile. Eine leere Zelle im
// Gitter laese sich als vergessene Nummer lesen; der Strich sagt "hier steht
// bewusst keine".
const EXCLUDED_POS_MARK = '—';

/**
 * Erzeugt das Angebots-PDF (A4) rein synchron im Firmen-Layout:
 *  - Seite 1: Anschreiben (Briefkopf, Anschriftfeld, Betreff, Einleitungstext,
 *    Gruss).
 *  - Seite 2: Kalkulation ohne eigenen Seitentitel — Projekt-Meta-Zeilen,
 *    frei definierbare Sektionen, Positionsliste, Zwischensummen und zwei
 *    Endsummen-Bloecke (ohne / inkl. NEP-Eventualpositionen).
 *
 * Signatur rueckwaertskompatibel erweitert: userName ist optional.
 */
export const generateQuotePDF = (
  quote: Quote,
  customer: Customer,
  company: CompanyInfo,
  userName?: string,
  // Persoenliche Unterschrift des Erstellers (dataURL PNG/JPEG) — wird im
  // Grussblock ueber dem Namen gerendert (wie handschriftlich unterschrieben).
  userSignature?: string,
  // skipCalculation: Kalkulationsseiten (Seite 2ff) entfallen — genutzt im
  // Modus "Original-LV bepreisen", wo das bepreiste Original die eigene
  // Kalkulation ersetzt. Zahlungsbedingungen/AGB/Datenschutz bleiben erhalten
  // (dann je auf eigener Seite).
  // __docFactory: Testnaht. Erlaubt es, dem Generator eine praeparierte
  // jsPDF-Instanz unterzuschieben (z.B. einen aufzeichnenden Proxy), um die
  // Zeichenbefehle zu charakterisieren. Verhaltensneutral: ohne dieses Feld
  // wird exakt dieselbe Instanz erzeugt wie zuvor. Notwendig, weil der
  // Generator seine Instanz sonst selbst anlegt und von aussen nicht
  // beobachtbar waere — siehe generatorQuote.golden.test.ts.
  // closingText: Grussformel aus InvoiceConfig — dieselbe Pflegestelle wie bei
  // Rechnung und Gutschrift. Fehlt sie (undefined), bleibt es beim bisherigen
  // 'Mit freundlichem Gruß'; leer gepflegt heisst "ohne Gruss".
  opts?: { skipCalculation?: boolean; closingText?: string; __docFactory?: () => jsPDF },
): jsPDF => {
  const doc = opts?.__docFactory
    ? opts.__docFactory()
    : new jsPDF({ format: 'a4', unit: 'mm', compress: true });
  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  const rightX = pageWidth - margin;

  const [tp0, tp1, tp2] = COLORS.text.primary;
  // BEWUSST primary, nicht secondary/light: das Angebot steht wie die
  // Rechnung komplett in EINEM Schwarz. Aliase bleiben, damit die
  // Zeichenstellen unveraendert lesbar sind.
  const [ts0, ts1, ts2] = COLORS.text.primary;
  const [tl0, tl1, tl2] = COLORS.text.primary;
  const [ln0, ln1, ln2] = COLORS.section.line;
  const GRAY = COLORS.text.primary; // NEP-Zeilen: nur noch italic, nicht grau

  // ── Briefpapier-Setup ──────────────────────────────────────────────────────
  // Ist ein Briefpapier hinterlegt, wird es als Ganzseiten-Hintergrund gestempelt
  // und der eigene Briefkopf/Fusszeile unterdrueckt. Seite 1 nutzt page1, alle
  // Folgeseiten following (Fallback: page1).
  const lh = createLetterhead(doc, company, A4_GEOMETRY);
  const { hasStationery, topFollow, botMargin, imgFmt, stampCurrentBg } = lh;

  // ══════════════════════════════════════════════════════════════════════════
  // SEITE 1 — ANSCHREIBEN
  // ══════════════════════════════════════════════════════════════════════════

  // Briefpapier-Hintergrund als erste Farbe auf Seite 1.
  stampCurrentBg();

  // ── Briefkopf Seite 1 (Logo, Trennlinie, Kopfspalten, DIN-Absenderzeile) ──
  // Vollstaendig in letterhead.ts; entfaellt dort automatisch bei Briefpapier.
  const { addressFieldY, rightColBottom } = drawPage1Header(doc, company, lh, A4_GEOMETRY);

  // ── Anschriftfeld (Empfaenger) — strukturierte Felder, sonst address ───────
  // Name in derselben Groesse wie Strasse und Ort (wie auf der Rechnung);
  // Zeilen 2/3 kommen aus dem Kundenstamm (name2/name3).
  let addrY = addressFieldY + 5;
  doc.setTextColor(tp0, tp1, tp2);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const nameLines = [customer.name, customer.name2, customer.name3]
    .map((n) => n?.trim())
    .filter((n): n is string => !!n)
    .flatMap((n) => doc.splitTextToSize(n, 85) as string[]);
  doc.text(nameLines, margin, addrY);
  addrY += nameLines.length * 5;
  const recipientLines: string[] = [];
  const hasStructured = customer.street || customer.zip || customer.city;
  if (hasStructured) {
    if (customer.street) recipientLines.push(customer.street);
    const cityLine = [customer.zip, customer.city].filter(Boolean).join(' ').trim();
    if (cityLine) recipientLines.push(cityLine);
  } else if (customer.address?.trim()) {
    recipientLines.push(...customer.address.replace(/,\s*/g, '\n').split('\n'));
  }
  recipientLines.forEach((line) => {
    doc.text(line, margin, addrY);
    addrY += 5;
  });

  // ── Ort + Datum (rechtsbuendig) ────────────────────────────────────────────
  const companyCity = cityFromAddress(company.address);
  const dateLine = companyCity
    ? `${companyCity}, ${longDate(quote.quoteDate)}`
    : longDate(quote.quoteDate);
  // Manueller Hoehenversatz des Anschreiben-Blocks (details.bodyOffsetMm, per
  // Regler im Angebotseditor). Er verschiebt Datumszeile, Betreff und alles
  // darunter GEMEINSAM — der Block bleibt in sich unveraendert, nur sein
  // Abstand zum Anschriftfeld aendert sich. Geklemmt auf -20..+60 mm, und die
  // Untergrenze addrY + 2 haelt den Block in jedem Fall unter der letzten
  // Empfaengerzeile: ein negativer Regler darf das Anschriftfeld nicht
  // ueberschreiben. Fehlt der Wert (Altbestand), ist er 0 = altes Layout.
  const bodyOffset = Math.max(-20, Math.min(60, Number(quote.details?.bodyOffsetMm) || 0));
  let y = Math.max(addrY + 2, Math.max(addrY + 6, addressFieldY + 30, rightColBottom + 8) + bodyOffset);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11.5);
  doc.setTextColor(tp0, tp1, tp2);
  doc.text(dateLine, rightX, y, { align: 'right' });
  y += 12;

  // ── Betreff ────────────────────────────────────────────────────────────────
  // Projektname direkt im Betreff: die wichtigste Zeile des Briefs traegt die
  // fuer den Empfaenger relevante Information, nicht nur die Verwaltungsnummer.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(tp0, tp1, tp2);
  const subject = quote.title?.trim()
    ? `Angebot Nr. ${quote.quoteNumber || ''} — ${quote.title.trim()}`
    : `Angebot - Nr. ${quote.quoteNumber || ''}`;
  const subjectLines = doc.splitTextToSize(subject, contentWidth) as string[];
  doc.text(subjectLines, margin, y);
  y += subjectLines.length * 6 + 1;

  if (customer.customerNumber?.trim()) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10.5);
    doc.setTextColor(tp0, tp1, tp2);
    doc.text(`Kunden-Nr.: ${customer.customerNumber.trim()}`, margin, y);
    y += 5.5;
  }
  y += 3;

  // Angebotsart: anpassbar ueber details.offerType (mehrzeilig via \n),
  // Default = bisheriger Firmenstandard.
  const offerType =
    quote.details?.offerType?.trim() || 'SiGeKo-Leistungen nach Baustellenverordnung\nHonorarangebot';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(tp0, tp1, tp2);
  offerType.split('\n').forEach((raw) => {
    const line = raw.trim();
    if (!line) return;
    const wrapped = doc.splitTextToSize(line, contentWidth) as string[];
    wrapped.forEach((w) => {
      doc.text(w, margin, y);
      y += 5.5;
    });
  });
  // Nachtragsangebot: Bezug auf den bestehenden Auftrag direkt unter der
  // Angebotsart („zu Auftrag AB … vom … · Projekt …"). Etwas kleiner und
  // normal gesetzt — die Angebotsart bleibt die Ueberschrift. Ohne Bezug
  // entfaellt die Zeile ganz (kein Schriftwechsel, Layout wie bisher).
  const supplementRef = quote.details?.nachtragZu;
  const supplementLine = supplementRef ? supplementPdfReferenceLine(supplementRef).trim() : '';
  if (supplementLine) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(tp0, tp1, tp2);
    const wrapped = doc.splitTextToSize(supplementLine, contentWidth) as string[];
    wrapped.forEach((w) => {
      doc.text(w, margin, y);
      y += 5;
    });
  }
  y += 3.5;

  // Seitenumbruch-Waechter Seite 1 — VOR der Blick-Box definiert, damit auch
  // sie ihn nutzen kann (lange Empfaenger-/Betreff-/Angebotsart-Texte koennen
  // y bereits ans Seitenende geschoben haben).
  const ensureSpaceP1 = (needed: number) => {
    const limit = hasStationery ? pageHeight - Math.max(botMargin, needed) : pageHeight - needed;
    if (y > limit) {
      doc.addPage();
      stampCurrentBg();
      y = hasStationery ? topFollow : margin + 5;
    }
  };

  // Die "Ihr Angebot auf einen Blick"-Box stand hier kurz (2026-08-01/02) und
  // wurde vom Betreiber wieder entfernt: zu viel Platz auf Seite 1.
  // details.glanceLines bleibt im Datenmodell erhalten.

  // ── Einleitungstext (mehrseitensicher) ─────────────────────────────────────
  if (quote.introText?.trim()) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(tp0, tp1, tp2);
    const introLines = doc.splitTextToSize(quote.introText.trim(), contentWidth);
    // Zeilenweise setzen, damit lange Einleitungen ueber Seiten umbrechen.
    for (const line of introLines) {
      ensureSpaceP1(25);
      doc.text(line, margin, y);
      y += 5;
    }
    y += 6;
  }

  // ── Schlusstext (outroText) — vor dem Gruss, mehrseitensicher ──────────────
  if (quote.outroText?.trim()) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(tp0, tp1, tp2);
    const outroLines = doc.splitTextToSize(quote.outroText.trim(), contentWidth) as string[];
    for (const line of outroLines) {
      ensureSpaceP1(25);
      doc.text(line, margin, y);
      y += 5;
    }
    y += 6;
  }

  // ── Gruss ──────────────────────────────────────────────────────────────────
  // Grussformel aus der Konfiguration statt hart verdrahtet — dieselbe
  // Pflegestelle wie bei Rechnung und Gutschrift (InvoiceConfig.closingText).
  //
  // Leer gepflegt heisst "ohne GRUSSZEILE" und sonst nichts. Bis 2026-08-24
  // fiel damit der ganze Block weg, Unterschrift eingeschlossen: wer die
  // Anrede weglassen wollte, verlor unbemerkt auch seine hinterlegte
  // Unterschrift. Die Unterschrift haengt an der Person, nicht an der Anrede.
  const closing = (opts?.closingText ?? 'Mit freundlichem Gruß').trim();
  ensureSpaceP1(30);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(tp0, tp1, tp2);
  if (closing) {
    doc.text(closing, margin, y);
    y += 4;
  }
  // Unterschrift ueber dem Namen (falls im Profil hinterlegt), sonst Leerraum.
  if (userSignature) {
    try {
      const sigProps = doc.getImageProperties(userSignature);
      const sigH = 14;
      const sigW = Math.min(50, (sigProps.width * sigH) / sigProps.height);
      ensureSpaceP1(45);
      doc.addImage(userSignature, imgFmt(userSignature), margin, y, sigW, sigH);
      y += sigH + 2;
    } catch (e) {
      console.warn('Angebots-PDF: Unterschrift konnte nicht eingebettet werden', e);
      y += 6;
    }
  } else {
    y += 6;
  }
  // Ersteller-Name direkt nach dem Gruss (z. B. "Ronny Ehlers"), Firma darunter.
  if (userName?.trim()) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(tp0, tp1, tp2);
    doc.text(userName.trim(), margin, y);
    y += 5;
  }
  if (company.name) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(ts0, ts1, ts2);
    doc.text(company.name, margin, y);
    y += 5;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // SEITE 2 — KALKULATION (entfaellt komplett bei opts.skipCalculation)
  // ══════════════════════════════════════════════════════════════════════════
  const skipCalculation = !!opts?.skipCalculation;
  // Summen-Modus (QuoteDetails.totalsMode): 'unitPrices' druckt nur
  // Einheitspreise — ohne Menge, Gesamtbetrag, Zwischen- und Endsummen (laufende
  // Leistungen ohne Enddatum, abgerechnet nach Aufwand). 'monthly' rechnet wie
  // bisher, benennt Betraege und Summen aber "je Monat".
  const totalsMode = quoteTotalsMode(quote);
  const unitOnly = totalsMode === 'unitPrices';
  const perMonth = totalsMode === 'monthly';
  let ky = hasStationery ? topFollow : margin;

  const ensureSpaceP2 = (needed: number) => {
    const limit = hasStationery ? pageHeight - Math.max(botMargin, needed) : pageHeight - needed;
    if (ky > limit) {
      doc.addPage();
      stampCurrentBg();
      ky = hasStationery ? topFollow : margin + 5;
    }
  };

  // Ohne Kalkulationsseiten zeigt ky auf den Anfang einer Folgeseite, die es
  // noch gar nicht gibt — der naechste Block wuerde ueber das Anschreiben von
  // Seite 1 zeichnen. Der erste Block nach der Kalkulation legt die Seite
  // deshalb selbst an; alle weiteren laufen normal darauf weiter.
  // Rueckgabe: true, wenn hier eine Seite begonnen wurde.
  let followPagePending = skipCalculation;
  const ensureFollowPage = (): boolean => {
    if (!followPagePending) return false;
    followPagePending = false;
    doc.addPage();
    stampCurrentBg();
    ky = hasStationery ? topFollow : margin + 5;
    return true;
  };

  // Der Kalkulationsblock bleibt bewusst unveraendert eingerueckt (Bug-Magnet-
  // Datei, minimaler Diff) — nur in diesen Guard gefasst.
  if (!skipCalculation) {
  doc.addPage();
  stampCurrentBg();

  // ── Kopf: kein eigener Seitentitel mehr.
  //
  // Hier stand "KALKULATION SiGe-KOORDINATION" mit Trennlinie darunter. Der
  // Titel benannte die Leistungsart fest, obwohl das Angebot auch andere
  // abdeckt, und wiederholte nur, was die Sektionsueberschriften und die
  // Meta-Zeile "Projekt: …" ohnehin sagen. Rechts oben steht ab Seite 2 der
  // Firmenname (globaler Seitenkopf am Ende) — kein Auftragnehmer-Block.
  //
  // Die Trennlinie ist mit entfallen: ohne Titel darueber saesse sie als
  // blosser Strich am oberen Rand und trennte nichts.

  // ── Meta-Zeilen links ──────────────────────────────────────────────────────
  const details = quote.details || {};
  const metaLines: string[] = [];
  if (customer.customerNumber?.trim()) metaLines.push(`Kunden-Nr.: ${customer.customerNumber.trim()}`);
  if (details.frameworkRef?.trim()) metaLines.push(`Rahmenvertrag: ${details.frameworkRef.trim()}`);
  if (quote.title?.trim()) metaLines.push(`Projekt: ${quote.title.trim()}`);
  if (totalsMode !== 'total' && details.projectStart && !details.projectEnd) {
    // Laufende Leistung ohne Enddatum: als Laufzeit benennen, statt nur ein
    // Startdatum hinzustellen, zu dem das Ende scheinbar vergessen wurde.
    metaLines.push(`Laufzeit: ab ${formatDate(details.projectStart)}, unbefristet`);
  } else {
    if (details.projectStart) metaLines.push(`Startdatum: ${formatDate(details.projectStart)}`);
    if (details.projectEnd) metaLines.push(`Enddatum: ${formatDate(details.projectEnd)}`);
  }
  // Mehrphasige Intervalle: je Phase eine Zeile + berechnete Gesamtanzahl.
  // Genau eine Phase: kein Intervall-Block (Start/Ende stehen bereits oben),
  // nur die Gesamtanzahl der Begehungen.
  // Ohne Phasen: einzeiliger Fallback aus intervalLabel/inspectionCount.
  if (details.intervalPhases && details.intervalPhases.length) {
    if (details.intervalPhases.length > 1) {
      metaLines.push('Intervalle:');
      details.intervalPhases.forEach((p) => metaLines.push(`  ${intervalSummary([p])}`));
    }
    metaLines.push(`Anzahl Begehungen: ${totalInspectionCount(details.intervalPhases)}`);
  } else {
    if (details.intervalLabel?.trim()) metaLines.push(`Intervall: ${details.intervalLabel.trim()}`);
    if (details.inspectionCount != null) metaLines.push(`Anzahl Begehungen: ${details.inspectionCount}`);
  }

  if (metaLines.length) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(tp0, tp1, tp2);
    metaLines.forEach((line) => {
      doc.text(line, margin, ky);
      ky += 5;
    });
    ky += 3;
  }

  // ── Sektionen + Positionen ─────────────────────────────────────────────────
  const sections: QuoteSection[] =
    details.sections && details.sections.length > 0 ? details.sections : DEFAULT_QUOTE_SECTIONS;

  // Positionsnummern (1.1.10, …) deterministisch aus Sektionen ableiten, damit
  // die Pos-Spalte immer konsistent zur Sektionsnummerierung ist.
  const numberedItems = computePosNumbers(sections, quote.items || []);

  // ── Positionsliste im Aufbau eines Langtext-Leistungsverzeichnisses ────────
  // Spalten: Pos. | Leistungsbeschreibung | Menge | Einheit | Einheitspreis |
  // Gesamtbetrag. Jede Position beginnt mit einer Kopfzeile, die nur Nummer und
  // Zahlen traegt; die Beschreibung steht eingerueckt DARUNTER (Titel fett,
  // Bemerkung/NEP-Hinweis als Folgezeilen). Kein Gitter, keine Bemerkungsspalte:
  // die Bemerkung gehoert zum Text der Position, nicht in eine Randspalte.
  const TABLE_FONT = 8.5;
  const PAD_X = 1.5;
  const COL_W = { pos: 16, quantity: 16, unit: 14, unitPrice: 22, total: 24 } as const;
  // Nur Einheitspreise: ohne Menge und Gesamtbetrag — die Beschreibung bekommt
  // deren Breite.
  const descColWidth = unitOnly
    ? contentWidth - (COL_W.pos + COL_W.unit + COL_W.unitPrice)
    : contentWidth - (COL_W.pos + COL_W.quantity + COL_W.unit + COL_W.unitPrice + COL_W.total);
  // Umbruchbreite der Beschreibung: Zellbreite minus Innenabstand, minus etwas
  // Luft, damit autoTable die vorgebrochenen Zeilen nie ein zweites Mal bricht.
  const descWrapWidth = descColWidth - 2 * PAD_X - 0.5;
  // Zeilenmarker fuer die Folgezeilen unter dem Titel. autoTable kennt nur EINE
  // Schrift je Zelle; die Zeilen werden deshalb in didDrawCell selbst gesetzt,
  // und der Marker sagt, welche Schrift eine Zeile bekommt. Titelzeilen tragen
  // keinen Marker.
  const SUB = '\u0001'; // normal
  const SUB_ITALIC = '\u0002'; // kursiv (NEP-Hinweis)
  const wrapDesc = (text: string, style: 'bold' | 'normal' | 'italic'): string[] => {
    doc.setFont('helvetica', style);
    doc.setFontSize(TABLE_FONT);
    return text
      .split(/\r\n|\r|\n/)
      .flatMap((line) => (line.trim() ? (doc.splitTextToSize(line, descWrapWidth) as string[]) : []));
  };
  // Formatierte Beschreibung (Fett/Kursiv/Farbe/Aufzaehlung, siehe
  // quoteRichText.ts): eine Zeile mischt mehrere Schriften, also traegt die
  // Zelle nur einen kurzen Platzhalter RICH + Index, und didDrawCell setzt die
  // vorab umbrochene Zeile aus richLines. Der Platzhalter ist schmal genug,
  // dass autoTable ihn nie selbst umbricht.
  const RICH = '\u0003';
  const RICH_LIST_INDENT = 3.5;
  const RICH_BULLET_X = 0.6;
  const richLines: QuoteRichLine[] = [];
  const measureRich = (text: string, style: 'normal' | 'bold' | 'italic' | 'bolditalic'): number => {
    doc.setFont('helvetica', style);
    doc.setFontSize(TABLE_FONT);
    return doc.getTextWidth(text);
  };
  const hexRgb = (hex: string): [number, number, number] | null => {
    const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
    return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
  };

  // Rechte Kante der Gesamtbetrag-Spalte (Blattrand minus Zellinnenabstand).
  // Zwischensummen und Endsummen richten sich daran aus, damit alle Betraege
  // buendig unter der Gesamtbetrag-Spalte stehen.
  const sumColRight = rightX - PAD_X;
  // Hoehe der Sektionsueberschrift inkl. Abstand zur Tabelle darunter.
  const SECTION_TITLE_H = 4;

  // Gruppen in Anzeigereihenfolge: definierte Sektionen, danach eine Rest-Gruppe
  // fuer Positionen ohne (bekannte) Sektion — damit nie Daten verloren gehen.
  type Group = { title: string; index: number; items: QuoteItem[] };
  const groups: Group[] = sections.map((s, idx) => ({
    title: s.title,
    index: idx + 1,
    items: numberedItems.filter((it) => it.sectionId === s.id),
  }));
  const knownIds = new Set(sections.map((s) => s.id));
  const leftover = numberedItems.filter((it) => !it.sectionId || !knownIds.has(it.sectionId));
  if (leftover.length) {
    groups.push({ title: 'Weitere Leistungen', index: groups.length + 1, items: leftover });
  }

  for (const group of groups) {
    if (group.items.length === 0) continue;

    // Gestrichene Positionen bleiben sichtbar (der Kunde soll sehen, welche
    // LV-Position wir NICHT anbieten), tragen aber keine Betraege. Optisch
    // laufen sie ueber dieselbe Kursiv-Kennzeichnung wie NEP-Zeilen.
    const quietFlags = group.items.map((it) => !!it.nep || isQuoteItemExcluded(it));
    // Beschreibung je Position: erste Zeile leer (dort stehen Nummer und
    // Zahlen), dann der Titel, dann Bemerkung und NEP-Hinweis. Vorgebrochen in
    // der Schrift, in der die Zeile spaeter gesetzt wird.
    const body = group.items.map((it, idx) => {
      const excluded = isQuoteItemExcluded(it);
      const line = lineTotal({
        quantity: it.quantity || 0,
        unitPrice: it.unitPrice || 0,
        discountPercent: it.discountPercent || 0,
      });
      // Gestrichene Positionen tragen keine interne Nummer (computePosNumbers
      // vergibt sie nur an aktive Zeilen; die aktiven zaehlen lueckenlos durch).
      // Ersatzweise die LV-Nummer des Auftraggebers in die Pos-Spalte zu setzen,
      // stellte zwei Nummernkreise untereinander — die Spalte bleibt deshalb bei
      // einem Strich, und der LV-Bezug haengt an der Bezeichnung, wo er als
      // Bezug lesbar ist.
      const lvRef = excluded && it.lvPosition?.trim() ? ` (LV-Pos. ${it.lvPosition.trim()})` : '';
      const rich = effectiveQuoteRich(it);
      let titleLines: string[];
      if (rich) {
        const blocks = lvRef
          ? rich.map((b, i) => (i === rich.length - 1 ? { ...b, runs: [...b.runs, { text: lvRef }] } : b))
          : rich;
        titleLines = layoutQuoteRich(blocks, descWrapWidth, measureRich, {
          listIndent: RICH_LIST_INDENT,
          quiet: quietFlags[idx],
        }).map((l) => {
          richLines.push(l);
          return RICH + (richLines.length - 1);
        });
      } else {
        titleLines = wrapDesc(`${it.title || ''}${lvRef}`, quietFlags[idx] ? 'italic' : 'bold');
      }
      const descLines = ['', ...titleLines];
      if (it.bemerkung?.trim()) {
        descLines.push(...wrapDesc(it.bemerkung.trim(), 'normal').map((l) => SUB + l));
      }
      // Eine gestrichene NEP-Position ist keine Eventualposition mehr: sie
      // steht in keiner der beiden Endsummen, weshalb der Summenblock weder
      // den Doppelblock noch die NEP-Fussnote ausgibt (siehe hasNep). Der
      // Hinweis hier stehen zu lassen, druckte ein Kuerzel ohne Legende ins
      // Kundendokument — und widerspraeche dem Editor, der in derselben Zeile
      // "entfällt" STATT "NEP" zeigt (QuoteEditor.tsx, gestrichene Zeile).
      if (it.nep && !excluded) {
        // Ohne Endsumme gibt es keine Summe, aus der die Position herausfiele.
        const nepHint = unitOnly
          ? 'NEP – Eventualposition, Ausführung nur auf gesonderte Anforderung'
          : perMonth
            ? 'NEP – Eventualposition, nicht in der Monatssumme enthalten'
            : 'NEP – Eventualposition, nicht in der Angebotssumme enthalten';
        descLines.push(...wrapDesc(nepHint, 'italic').map((l) => SUB_ITALIC + l));
      }
      const posCell = excluded ? EXCLUDED_POS_MARK : it.posNumber || it.lvPosition || '';
      // Nur Einheitspreise: Listenpreis wie im Standardmodus, ein
      // Positionsrabatt steht ausdruecklich darunter. Ohne Gesamtbetrag gaebe
      // es sonst keine Stelle, an der er sichtbar wuerde — und ein vorab
      // rabattierter Einheitspreis muesste gerundet werden, waehrend die
      // Abrechnung (lineTotal) erst nach der Menge rundet.
      if (unitOnly) {
        if (!excluded && (it.discountPercent || 0) > 0) {
          descLines.push(
            ...wrapDesc(`abzgl. ${fmtPercentDE(it.discountPercent || 0)} % Rabatt auf den Einheitspreis`, 'normal').map(
              (l) => SUB + l,
            ),
          );
        }
        return [
          posCell,
          descLines.join('\n'),
          it.unit || '',
          excluded ? QUOTE_EXCLUDED_LABEL : fmtNum(it.unitPrice || 0),
        ];
      }
      return [
        posCell,
        descLines.join('\n'),
        fmtNum(it.quantity || 0),
        it.unit || '',
        excluded ? QUOTE_EXCLUDED_LABEL : fmtNum(it.unitPrice || 0),
        excluded ? QUOTE_EXCLUDED_LABEL : fmtNum(line),
      ];
    });

    // Zeilenmass der Beschreibung — dieselbe Rechnung wie autoTable intern
    // (Schriftgrad in mm mal Zeilenfaktor), sonst laufen selbst gesetzte Zeilen
    // aus der von autoTable bemessenen Zelle heraus.
    const fontMm = TABLE_FONT / doc.internal.scaleFactor;
    const lineH = fontMm * doc.getLineHeightFactor();

    // Sektionsueberschrift — aber erst, wenn Tabellenkopf UND erste Position
    // darunter noch auf die Seite passen. Vorher stand hier ein pauschales
    // ensureSpaceP2(30): rowPageBreak 'avoid' schob dann eine lange erste
    // Position komplett auf die naechste Seite und liess Ueberschrift und
    // Tabellenkopf verwaist zurueck.
    //
    // Dieselbe Entscheidung wie autoTable (shouldPrintOnCurrentPage): passt die
    // Position nicht mehr in den Rest der Seite, wandert sie GANZ auf die
    // naechste — es sei denn, sie ist hoeher als eine leere Seite; dann wird
    // sie geteilt und bleibt hier. Nur im ersten Fall muss die Ueberschrift
    // mitwandern. Hoehen wie autoTable: Zeilen mal Zeilenmass plus
    // Zellinnenabstand; der Kopf hat zwei Zeilen in 8 pt.
    //
    // "Leere Seite" muss fuer beide dasselbe heissen. Nach einem eigenen
    // Umbruch steht die Tabelle unter der Ueberschrift bei tableTop; autoTable
    // bekommt deshalb genau diesen Wert als oberen Rand (Folgeseiten und
    // Hoechsthoehe einer Position). Rechnete autoTable mit einem kleineren
    // Rand, gaebe es ein Band von Positionshoehen, die nach seiner Rechnung
    // auf eine leere Seite passen, unter der Ueberschrift aber nicht mehr:
    // die Ueberschrift wanderte auf eine neue Seite, die Position auf die
    // uebernaechste, und dazwischen bliebe eine fast leere Seite.
    const firstRowLines = body[0][1].split('\n').length;
    const firstRowH = firstRowLines * lineH + 2.2 + 3.2;
    const headH = 2 * (8 / doc.internal.scaleFactor) * doc.getLineHeightFactor() + 1.5 + 1.8;
    const bottomReserve = hasStationery ? botMargin : 25;
    const tableTop = (hasStationery ? topFollow : margin + 5) + SECTION_TITLE_H;
    const maxRowH = pageHeight - tableTop - bottomReserve - headH;
    ensureSpaceP2(
      firstRowH > maxRowH ? 30 : SECTION_TITLE_H + headH + firstRowH + bottomReserve,
    );
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(tp0, tp1, tp2);
    doc.text(`${group.index}. ${group.title.toUpperCase()}`, margin, ky);
    ky += SECTION_TITLE_H;

    autoTable(doc, {
      startY: ky,
      head: [
        unitOnly
          ? ['Pos.', 'Leistungsbeschreibung', 'Einheit', 'Einheitspreis\nin EUR']
          : [
              'Pos.',
              'Leistungsbeschreibung',
              'Menge',
              'Einheit',
              'Einheitspreis\nin EUR',
              perMonth ? 'Monatsbetrag\nin EUR' : 'Gesamtbetrag\nin EUR',
            ],
      ],
      body,
      // 'plain' statt 'grid': ein Leistungsverzeichnis ist kein Kasten-Gitter.
      // Kopfzeile mit Strich darunter, Positionen durch Abstand getrennt,
      // Abschluss mit duenner Linie; alles in didDrawCell selbst gezeichnet.
      theme: 'plain',
      showHead: 'everyPage',
      // Eine Position soll moeglichst nicht ueber den Seitenumbruch gerissen
      // werden (Zahlen oben, Beschreibung auf der naechsten Seite). Passt sie
      // auch auf eine leere Seite nicht, teilt autoTable sie trotzdem.
      rowPageBreak: 'avoid',
      // Bei Briefpapier: obere/untere Raender respektieren, damit autoTable-eigene
      // Seitenumbrueche innerhalb der Briefpapier-Raender bleiben.
      // Ohne Briefpapier: unteren Rand reservieren, damit die Tabelle NICHT in die
      // Fusszeile (Seitenzahl bei pageHeight-20, Linie -15, Footertext -10) laeuft
      // und die Seitenzahl das LV verdeckt. 25 mm laesst 5 mm Luft ueber der Zahl.
      // top = tableTop (siehe oben): Folgeseiten beginnen dort, wo die Tabelle
      // auch nach einem eigenen Umbruch beginnt, und die Hoechsthoehe einer
      // Position ist damit dieselbe wie in der Ueberschriften-Rechnung.
      margin: { left: margin, right: margin, top: tableTop, bottom: bottomReserve },
      // autoTable legt Seiten selbst an: Hintergrund VOR dem Tabelleninhalt
      // stempeln. Die Startseite ist bereits gestempelt (Dedup greift).
      willDrawPage: hasStationery ? () => stampCurrentBg() : undefined,
      headStyles: {
        textColor: [ts0, ts1, ts2],
        fontSize: 8,
        fontStyle: 'bold',
        lineWidth: 0,
        valign: 'bottom',
        cellPadding: { top: 1.5, bottom: 1.8, left: PAD_X, right: PAD_X },
      },
      styles: {
        fontSize: TABLE_FONT,
        textColor: [tp0, tp1, tp2],
        // Unten mehr Luft als oben: der Abstand trennt die Positionen
        // voneinander, wo frueher das Gitter stand.
        cellPadding: { top: 2.2, bottom: 3.2, left: PAD_X, right: PAD_X },
        lineWidth: 0,
        valign: 'top',
        overflow: 'linebreak',
      },
      columnStyles: unitOnly
        ? {
            0: { cellWidth: COL_W.pos },
            1: { cellWidth: descColWidth },
            2: { cellWidth: COL_W.unit, halign: 'center' },
            // Der Einheitspreis ist hier der Betrag, der zaehlt: fett wie
            // sonst der Gesamtbetrag.
            3: { cellWidth: COL_W.unitPrice, halign: 'right', fontStyle: 'bold' },
          }
        : {
            0: { cellWidth: COL_W.pos },
            1: { cellWidth: descColWidth },
            2: { cellWidth: COL_W.quantity, halign: 'right' },
            3: { cellWidth: COL_W.unit, halign: 'center' },
            4: { cellWidth: COL_W.unitPrice, halign: 'right' },
            5: { cellWidth: COL_W.total, halign: 'right', fontStyle: 'bold' },
          },
      didParseCell: (data) => {
        // Kopfzeile von Menge/Einheit/EP/Gesamt an der Kante der Werte
        // ausrichten: columnStyles greift in autoTable nicht auf den head
        // (gleiche Technik wie im Rechnungsgenerator).
        if (data.section === 'head') {
          const idx = data.column.index;
          if (unitOnly) {
            if (idx === 2) data.cell.styles.halign = 'center';
            if (idx === 3) data.cell.styles.halign = 'right';
          } else {
            if (idx === 3) data.cell.styles.halign = 'center';
            if (idx === 2 || idx >= 4) data.cell.styles.halign = 'right';
          }
          return;
        }
        // NEP- und gestrichene Zeilen kursiv (Beschreibung: siehe didDrawCell).
        if (data.section === 'body' && quietFlags[data.row.index]) {
          data.cell.styles.fontStyle = 'italic';
          data.cell.styles.textColor = [GRAY[0], GRAY[1], GRAY[2]];
        }
      },
      // Die Beschreibung setzt der Generator selbst: autoTable soll die Zelle
      // nur bemessen (und bei Bedarf ueber Seiten teilen), aber nichts
      // hineinschreiben. Der Text der Zelle wird deshalb kurz vor dem Zeichnen
      // beiseitegelegt und geleert.
      willDrawCell: (data) => {
        if (data.section !== 'body' || data.column.index !== 1) return;
        const lines = data.cell.text as string[];
        (data.cell as unknown as { __descLines?: string[] }).__descLines = lines;
        data.cell.text = [];
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 1) {
          // Zeilen dieses Zellen-Fragments (bei Seitenteilung nur der Rest).
          const lines = (data.cell as unknown as { __descLines?: string[] }).__descLines ?? [];
          // NICHT ueber quietFlags[data.row.index]: die Fortsetzung einer ueber
          // Seiten geteilten Position traegt in autoTable den Zeilenindex -1.
          // didParseCell hat die Zelle kursiv gestellt, und die Styles wandern
          // mit in die Fortsetzungszelle — sie sind die verlaessliche Quelle.
          const quiet = data.cell.styles.fontStyle === 'italic';
          const x = data.cell.x + data.cell.padding('left');
          // Grundlinie der ersten Zeile wie autoTableText: Oberkante plus
          // Schriftgrad mal (2 - 1.15).
          let ly = data.cell.y + data.cell.padding('top') + fontMm * 0.85;
          doc.setFontSize(TABLE_FONT);
          doc.setTextColor(tp0, tp1, tp2);
          for (const raw of lines) {
            if (raw.startsWith(RICH)) {
              // Schrift und Farbe kommen aus der Formatierung; quiet ist beim
              // Umbrechen schon eingerechnet (kursiv, ohne Farbe).
              const rl = richLines[Number(raw.slice(1))];
              if (rl?.bullet) {
                doc.setFont('helvetica', quiet ? 'italic' : 'normal');
                doc.text('•', x + RICH_BULLET_X, ly);
              }
              for (const seg of rl?.segments ?? []) {
                const rgb = seg.color ? hexRgb(seg.color) : null;
                if (rgb) doc.setTextColor(rgb[0], rgb[1], rgb[2]);
                else doc.setTextColor(tp0, tp1, tp2);
                doc.setFont('helvetica', seg.fontStyle);
                doc.text(seg.text, x + seg.x, ly);
              }
              doc.setTextColor(tp0, tp1, tp2);
            } else if (raw.startsWith(SUB)) {
              doc.setFont('helvetica', quiet ? 'italic' : 'normal');
              doc.text(raw.slice(1), x, ly);
            } else if (raw.startsWith(SUB_ITALIC)) {
              doc.setFont('helvetica', 'italic');
              doc.text(raw.slice(1), x, ly);
            } else if (raw) {
              doc.setFont('helvetica', quiet ? 'italic' : 'bold');
              doc.text(raw, x, ly);
            }
            ly += lineH;
          }
        }
        // Genau EINE Linie unter dem Kopf, gezeichnet an der letzten Spalte
        // (an jeder Zelle laege sie sechsfach uebereinander).
        if (data.section !== 'head' || data.column.index !== data.table.columns.length - 1) return;
        const lineY = data.cell.y + data.cell.height;
        doc.setDrawColor(ln0, ln1, ln2);
        doc.setLineWidth(0.3);
        doc.line(margin, lineY, rightX, lineY);
      },
    });

    // Duenne Abschlusslinie unter der letzten Position — am tatsaechlichen
    // Tabellenende (finalY), nicht an der letzten Zeile: wird die letzte
    // Position ueber Seiten geteilt, saesse der Strich sonst am Seitenende
    // ihres ersten Fragments und fehlte unter der Fortsetzung.
    const tableEnd = (doc as any).lastAutoTable?.finalY as number | undefined;
    if (tableEnd != null) {
      doc.setDrawColor(ln0, ln1, ln2);
      doc.setLineWidth(0.1);
      doc.line(margin, tableEnd, rightX, tableEnd);
    }
    ky = (tableEnd ?? ky) + 4;

    // Zwischensumme der Sektion (ohne NEP und ohne gestrichene Positionen —
    // dieselbe Auswahl wie in calculateQuoteTotalsDetailed, sonst passte die
    // Summe der Zwischensummen nicht zur Endsumme).
    const subtotal = round2(
      group.items
        .filter((it) => !it.nep && isQuoteItemActive(it))
        .reduce(
          (sum, it) =>
            sum +
            lineTotal({
              quantity: it.quantity || 0,
              unitPrice: it.unitPrice || 0,
              discountPercent: it.discountPercent || 0,
            }),
          0,
        ),
    );
    // Ist der ganze Abschnitt gestrichen, gibt es keine Zwischensumme mehr:
    // "0,00 €" behauptete eine Leistung zum Nulltarif. Die Zeile bleibt stehen
    // (jeder Abschnitt schliesst mit ihr ab — sie wegzulassen risse ein Loch in
    // die Tabellenfolge) und traegt statt des Betrags dasselbe "entfällt" wie
    // die Positionszeilen darueber. Ein reiner NEP-Abschnitt ist NICHT gemeint:
    // seine Leistungen sind angeboten, nur nicht eingerechnet — dort bleibt es
    // wie bisher bei 0,00 €.
    const sectionStruck = group.items.every(isQuoteItemExcluded);
    // Nur Einheitspreise: keine Zwischensumme — nur der Abstand zum naechsten
    // Abschnitt bleibt.
    if (unitOnly) {
      ky += 4;
      continue;
    }
    ensureSpaceP2(20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(ts0, ts1, ts2);
    doc.text(`Zwischensumme ${group.title}${perMonth ? ' je Monat' : ''}`, margin, ky);
    doc.setTextColor(tp0, tp1, tp2);
    doc.text(sectionStruck ? QUOTE_EXCLUDED_LABEL : fmtEur(subtotal), sumColRight, ky, { align: 'right' });
    ky += 8;
  }

  // ── Endsummen ──────────────────────────────────────────────────────────────
  const detailed = calculateQuoteTotalsDetailed(
    quote.items || [],
    quote.discountPercent || 0,
    quote.taxRate || 0,
  );
  const taxLabel = fmtNum(quote.taxRate || 0);

  // Endsummen-Block im kaufmaennischen Standard: Netto, zzgl. MwSt., Trennstrich,
  // Bruttobetrag fett mit doppeltem Abschlussstrich. Betraege buendig unter der
  // Gesamtbetrag-Spalte (sumColRight), buendig mit den Positionsbetraegen.
  const labelX = margin + 55;
  const valueX = sumColRight;
  const sumRow = (
    label: string,
    value: string,
    opts?: { bold?: boolean; color?: number[]; size?: number },
  ) => {
    ensureSpaceP2(20);
    doc.setFont('helvetica', opts?.bold ? 'bold' : 'normal');
    doc.setFontSize(opts?.size ?? 10);
    const c = opts?.color ?? COLORS.text.primary;
    doc.setTextColor(c[0], c[1], c[2]);
    doc.text(label, labelX, ky);
    doc.text(value, valueX, ky, { align: 'right' });
    ky += opts?.size && opts.size > 10 ? 7 : 6;
  };
  // Doppelter Abschlussstrich unter dem Bruttobetrag (kaufmaennische Konvention).
  const closingRule = () => {
    doc.setDrawColor(tp0, tp1, tp2);
    doc.setLineWidth(0.4);
    doc.line(labelX, ky - 4.5, valueX, ky - 4.5);
    doc.setLineWidth(0.2);
    doc.line(labelX, ky - 3.5, valueX, ky - 3.5);
  };
  // NEP-Darstellung: 'auto' (Default) zeigt beide Bloecke nur bei vorhandenen
  // NEP-Positionen, 'both' immer, 'simple' nie. Ohne Doppelblock wird ein
  // schlichter Summenblock (Werte = ohne NEP) ohne "(ohne NEP)"-Zusatz und ohne
  // Fussnote ausgegeben.
  // Eine gestrichene NEP-Position ist keine Eventualposition mehr: sie steht in
  // keiner der beiden Endsummen und darf den Doppelblock samt Fussnote nicht
  // ausloesen.
  const hasNep = (quote.items || []).some((it) => !!it.nep && isQuoteItemActive(it));
  const nepMode = quote.details?.nepDisplay ?? 'auto';
  const showDual = nepMode === 'both' || (nepMode === 'auto' && hasNep);
  // Summenbezeichnung: bei 'monthly' ausdruecklich "je Monat", damit der
  // Monatsbetrag nie als Gesamtpreis gelesen wird.
  const L = (total: string, month: string) => (perMonth ? month : total);

  if (unitOnly) {
    // Statt Endsumme: die Preisgrundlage in drei Saetzen. Ein Gesamtrabatt
    // haette keine Summe, an der er stuende — er wird hier ausgesprochen.
    const notes = [
      (quote.taxRate || 0) > 0 ? `Alle Preise netto zzgl. ${fmtPercentDE(quote.taxRate || 0)} % MwSt.` : 'Alle Preise netto.',
      ...((quote.discountPercent || 0) > 0
        ? [`Auf alle Einheitspreise gewähren wir ${fmtPercentDE(quote.discountPercent || 0)} % Rabatt.`]
        : []),
      'Abgerechnet wird nach tatsächlich erbrachter Leistung.',
    ];
    ensureSpaceP2(15 + notes.length * 5);
    doc.setDrawColor(ln0, ln1, ln2);
    doc.setLineWidth(0.2);
    doc.line(margin, ky - 2, rightX, ky - 2);
    ky += 3;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(tp0, tp1, tp2);
    for (const n of notes) {
      ensureSpaceP2(15);
      doc.text(n, margin, ky);
      ky += 5;
    }
  } else if (showDual) {
    // Sicherstellen, dass der komplette (ohne-NEP) Block zusammen bleibt.
    ensureSpaceP2(45);
    doc.setDrawColor(tp0, tp1, tp2);
    doc.setLineWidth(0.4);
    doc.line(labelX, ky - 2, valueX, ky - 2);
    ky += 2;

    // B2B: Netto ist die massgebliche Angebotssumme (fett, Abschlussstrich);
    // MwSt. und Brutto darunter nachrichtlich.
    sumRow(L('Gesamtsumme netto (ohne NEP)', 'Summe netto je Monat (ohne NEP)'), fmtEur(detailed.totalsExclNep.netTotal), {
      bold: true,
      size: 11.5,
    });
    closingRule();
    ky += 1;
    sumRow(`zzgl. ${taxLabel} % MwSt.`, fmtEur(detailed.totalsExclNep.taxAmount), {
      color: COLORS.text.primary,
    });
    sumRow(L('Gesamtbetrag brutto (ohne NEP)', 'Summe brutto je Monat (ohne NEP)'), fmtEur(detailed.totalsExclNep.grossTotal), {
      bold: true,
      color: COLORS.text.primary,
    });

    ky += 4;

    // ── NEP-Fussnote ─────────────────────────────────────────────────────────
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.setTextColor(tl0, tl1, tl2);
    const footnote =
      `NEP = nicht eingerechnete (Eventual-)Position – Einzelpreis ausgewiesen, aber nicht in der ${L('Endsumme', 'Monatssumme')} (ohne NEP) enthalten.`;
    const fnLines = doc.splitTextToSize(footnote, contentWidth);
    ensureSpaceP2(fnLines.length * 4 + 30);
    doc.text(fnLines, margin, ky);
    ky += fnLines.length * 4 + 6;

    // ── Endsummen inkl. NEP (nachrichtlich) ──────────────────────────────────
    ensureSpaceP2(35);
    doc.setDrawColor(ln0, ln1, ln2);
    doc.setLineWidth(0.2);
    doc.line(labelX, ky - 2, valueX, ky - 2);
    ky += 2;

    sumRow(L('Gesamtsumme netto (inkl. NEP)', 'Summe netto je Monat (inkl. NEP)'), fmtEur(detailed.totalsInclNep.netTotal), {
      color: COLORS.text.primary,
    });
    sumRow(`zzgl. ${taxLabel} % MwSt.`, fmtEur(detailed.totalsInclNep.taxAmount), {
      color: COLORS.text.primary,
    });
    sumRow(L('Gesamtbetrag brutto (inkl. NEP)', 'Summe brutto je Monat (inkl. NEP)'), fmtEur(detailed.totalsInclNep.grossTotal), {
      bold: true,
      color: COLORS.text.primary,
    });
  } else {
    // Schlichter Einzelblock (keine NEP-Erwaehnung, keine Fussnote).
    ensureSpaceP2(45);
    doc.setDrawColor(tp0, tp1, tp2);
    doc.setLineWidth(0.4);
    doc.line(labelX, ky - 2, valueX, ky - 2);
    ky += 2;

    // B2B: Netto ist die massgebliche Angebotssumme (fett, Abschlussstrich);
    // MwSt. und Brutto darunter nachrichtlich.
    sumRow(L('Gesamtsumme netto', 'Summe netto je Monat'), fmtEur(detailed.totalsExclNep.netTotal), {
      bold: true,
      size: 11.5,
    });
    closingRule();
    ky += 1;
    sumRow(`zzgl. ${taxLabel} % MwSt.`, fmtEur(detailed.totalsExclNep.taxAmount), {
      color: COLORS.text.primary,
    });
    sumRow(L('Gesamtbetrag brutto', 'Summe brutto je Monat'), fmtEur(detailed.totalsExclNep.grossTotal), {
      bold: true,
      color: COLORS.text.primary,
    });
  }

  // ── Kontext direkt am Preis: Inklusiv-Hinweis (nur aus den Daten) + Frist ──
  // Der Inklusiv-Hinweis kommt AUSSCHLIESSLICH aus details.inclusiveNote: eine
  // fest einkompilierte Zusage stuende auch auf Angeboten, die Fahrtkosten
  // separat oder als NEP kalkulieren — und veraenderte den Vertragsumfang.
  ky += 3;
  ensureSpaceP2(20);
  const inclusiveNote = quote.details?.inclusiveNote?.trim();
  if (inclusiveNote) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(ts0, ts1, ts2);
    const noteLines = doc.splitTextToSize(inclusiveNote, contentWidth) as string[];
    noteLines.forEach((line) => {
      ensureSpaceP2(15);
      doc.text(line, margin, ky);
      ky += 5;
    });
  }
  } // Ende if (!skipCalculation)

  // ── Angebotsfrist ─────────────────────────────────────────────────────────
  // Gemeinsamer Dokumentteil: die Bindefrist ist eine Aussage zum Angebot, nicht
  // zur Kalkulation — sie muss auch im Modus "Original-LV bepreisen" erscheinen.
  if (quote.validUntil) {
    ensureFollowPage();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(tp0, tp1, tp2);
    doc.text(`Dieses Angebot ist gültig bis zum ${longDate(quote.validUntil)}.`, margin, ky);
    ky += 5;
  }

  // ── Zahlungsbedingungen (nach den Endsummen, vor den AGB) ──────────────────
  if (quote.paymentTerms?.trim()) {
    // Ohne Kalkulationsseiten: eigene Seite (gleiches Muster wie AGB) — sofern
    // sie nicht schon von der Angebotsfrist begonnen wurde.
    if (!ensureFollowPage()) {
      ky += 8;
      ensureSpaceP2(25);
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(tp0, tp1, tp2);
    doc.text('Zahlungsbedingungen', margin, ky);
    ky += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(tp0, tp1, tp2);
    const payLines = doc.splitTextToSize(quote.paymentTerms.trim(), contentWidth) as string[];
    for (const line of payLines) {
      ensureSpaceP2(20);
      doc.text(line, margin, ky);
      ky += 4.5;
    }
  }

  // ── Auftragserteilung (Annahmeblock) ───────────────────────────────────────
  // Handlungsaufforderung statt Juristerei als letzter Eindruck: Gegenzeichnen
  // genuegt, der Kunde muss keine Antwort formulieren.
  {
    // Auf einer frisch begonnenen Seite entfaellt der Abstand zum Vorgaenger.
    if (!ensureFollowPage()) ky += 10;
    ensureSpaceP2(55);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(tp0, tp1, tp2);
    doc.text('Auftragserteilung', margin, ky);
    ky += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(
      `Hiermit erteilen wir den Auftrag gemäß Angebot Nr. ${quote.quoteNumber || ''}.`,
      margin,
      ky,
    );
    ky += 18;
    doc.setDrawColor(tp0, tp1, tp2);
    doc.setLineWidth(0.2);
    doc.line(margin, ky, margin + 60, ky);
    doc.line(margin + 90, ky, margin + 170, ky);
    ky += 4;
    doc.setFontSize(7.5);
    doc.setTextColor(ts0, ts1, ts2);
    doc.text('Ort, Datum', margin, ky);
    doc.text('Unterschrift / Firmenstempel Auftraggeber', margin + 90, ky);
    ky += 5;
  }

  // ── AGB (optional, ganz am Ende der Kalkulation) ───────────────────────────
  // Nach Zahlungsbedingungen/Endsummen; Ueberschrift + Freitext zeilenweise,
  // damit lange AGB ueber Seiten umbrechen (gleiches Muster wie Einleitungstext).
  if (quote.details?.agbText?.trim()) {
    // AGB immer auf eigener Seite.
    doc.addPage();
    stampCurrentBg();
    ky = hasStationery ? topFollow : margin + 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(tp0, tp1, tp2);
    doc.text('AGB', margin, ky);
    ky += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(tp0, tp1, tp2);
    const agbLines = doc.splitTextToSize(quote.details.agbText.trim(), contentWidth);
    for (const line of agbLines) {
      ensureSpaceP2(20);
      doc.text(line, margin, ky);
      ky += 4.5;
    }
  }

  // ── Datenschutzinformation (optional, eigene Seite nach den AGB) ───────────
  if (quote.details?.privacyText?.trim()) {
    doc.addPage();
    stampCurrentBg();
    ky = hasStationery ? topFollow : margin + 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(tp0, tp1, tp2);
    doc.text('Datenschutzinformation', margin, ky);
    ky += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(tp0, tp1, tp2);
    const privacyLines = doc.splitTextToSize(quote.details.privacyText.trim(), contentWidth) as string[];
    for (const line of privacyLines) {
      ensureSpaceP2(20);
      doc.text(line, margin, ky);
      ky += 4.5;
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // FOOTER auf allen Seiten
  // ══════════════════════════════════════════════════════════════════════════
  drawFooters(doc, company, lh, A4_GEOMETRY);

  return doc;
};
